import { useEffect, useState } from "react";
import { Lock, Smartphone, Shield } from "lucide-react";
import { AuthCard } from "@/components/shell/auth-card";
import { Button } from "@/components/ui/button";
import { fetchAuthOptions, type AuthMethod } from "@/lib/api";

// Restyled per the SSO login card direction (icon medallion, flat full-width
// provider buttons, divider + security helper row) while preserving this
// page's own real logic untouched: methods still come from fetchAuthOptions
// (backend-configured per org, not a hardcoded provider list), disabled
// methods are still surfaced, and startLogin still does a real
// window.location.assign redirect. Each method button is now directly
// actionable (click = start that method's login) instead of the previous
// select-a-tile-then-press-Continue two-step, matching how every provider
// button behaves in the Figma reference — this is the one real interaction
// change, not just a style pass.

type LoginState = "loading" | "login" | "redirecting";

function iconForMethod(method: AuthMethod) {
  if (/google/i.test(method.label)) return { Icon: GoogleIcon };
  if (/microsoft|azure|entra/i.test(method.label)) return { Icon: MicrosoftIcon };
  if (/okta/i.test(method.label)) return { Icon: OktaIcon };
  if (/saml/i.test(method.label) || /sso/i.test(method.label)) return { Icon: Shield };
  if (/authenticator|totp/i.test(method.label)) return { Icon: Smartphone };
  return { Icon: Shield };
}

function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="var(--ads-brand-google-blue)"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="var(--ads-brand-google-green)"/>
      <path d="M5.84 14.09A6.68 6.68 0 0 1 5.5 12c0-.72.13-1.43.34-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.84z" fill="var(--ads-brand-google-yellow)"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="var(--ads-brand-google-red)"/>
    </svg>
  );
}

function MicrosoftIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="1" y="1" width="10.5" height="10.5" fill="var(--ads-brand-microsoft-red)" />
      <rect x="12.5" y="1" width="10.5" height="10.5" fill="var(--ads-brand-microsoft-green)" />
      <rect x="1" y="12.5" width="10.5" height="10.5" fill="var(--ads-brand-microsoft-blue)" />
      <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="var(--ads-brand-microsoft-yellow)" />
    </svg>
  );
}

function OktaIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="12" fill="var(--ads-ink)" />
      <circle cx="12" cy="12" r="6.5" fill="none" stroke="#fff" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="2" fill="#fff" />
    </svg>
  );
}

export default function LoginPage() {
  const [methods, setMethods] = useState<AuthMethod[]>([]);
  const [state, setState] = useState<LoginState>("loading");
  const [pendingId, setPendingId] = useState<string | null>(null);

  useEffect(() => {
    fetchAuthOptions("/").then((m) => {
      setMethods(m);
      setState("login");
    });
  }, []);

  function startLogin(method: AuthMethod) {
    if (!method.startUrl || pendingId) return;
    setPendingId(method.id);
    setState("redirecting");
    window.location.assign(method.startUrl);
  }

  const enabled = methods.filter((m) => m.enabled);
  const disabled = methods.filter((m) => !m.enabled);

  return (
    <AuthCard>
      <div className="auth-icon auth-icon--sso" aria-hidden="true">
        <div className="auth-icon__inner">
          <Lock size={28} strokeWidth={1.25} />
        </div>
      </div>

      <div className="auth-heading">
        <h1 className="auth-title">Sign in to Alloy</h1>
        <p className="auth-subtitle">Sign in with your organization&apos;s Single Sign-On to continue.</p>
      </div>

      {state === "loading" && (
        <div className="flex w-full items-center justify-center py-8">
          <div className="spinner" aria-label="Loading sign-in options" />
        </div>
      )}

      {state === "login" && (
        <div className="auth-block">
          <div className="flex w-full flex-col gap-5">
            {enabled.map((method) => {
              const { Icon } = iconForMethod(method);
              return (
                <Button
                  key={method.id}
                  variant="ghost"
                  size="lg"
                  className="w-full justify-center gap-2 bg-bg hover:bg-disabled-soft focus-visible:bg-bg disabled:bg-bg"
                  loading={pendingId === method.id}
                  disabled={pendingId !== null && pendingId !== method.id}
                  aria-label={method.label}
                  onClick={() => startLogin(method)}
                >
                  <Icon size={20} />
                  {method.label}
                </Button>
              );
            })}
            {enabled.length === 0 && (
              <p className="w-full rounded-lg border border-line bg-bg px-4 py-3 text-center text-sm text-muted">
                No sign-in methods are enabled for this organization.
              </p>
            )}

            {disabled.length > 0 && (
              <details className="w-full text-xs text-muted">
                <summary className="cursor-pointer font-medium hover:text-text">Other methods (not enabled)</summary>
                <div className="mt-2 flex flex-col gap-2">
                  {disabled.map((method) => (
                    <div key={method.id} className="flex items-center gap-2 rounded-md border border-line px-3 py-2 opacity-50">
                      <span className="font-medium text-text">{method.label}</span>
                      <span className="ml-auto text-xs text-muted">Not enabled</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}

      {state === "redirecting" && (
        <div className="flex w-full flex-col items-center gap-4 text-center" data-testid="login-redirecting">
          <div className="auth-heading" style={{ width: "100%" }}>
            <div className="auth-title" style={{ fontSize: 26, letterSpacing: "-0.52px" }}>
              Redirecting to your identity provider
            </div>
            <div className="auth-subtitle" style={{ fontSize: 14, lineHeight: "normal" }}>
              You&apos;ll sign in with your organization&apos;s account, then return here automatically.
            </div>
          </div>
          <div className="spinner" aria-hidden />
        </div>
      )}

      {state === "login" && (
        <>
          <div className="h-px w-full shrink-0 rounded-full bg-line-soft" />
          <div className="flex w-full items-center justify-center gap-3.5">
            <span className="grid size-11 shrink-0 place-items-center rounded-full border border-disabled-soft bg-bg shadow-[0_2px_4px_rgba(27,28,29,0.04)]">
              <Lock size={18} strokeWidth={1.5} className="text-muted" aria-hidden />
            </span>
            <p className="flex-1 text-center text-[18px] leading-6 tracking-[-0.36px] text-text">
              You&apos;ll be securely redirected to your organization&apos;s identity provider.
            </p>
          </div>
        </>
      )}
    </AuthCard>
  );
}
